import { CalendarIcon, ClockIcon, ExclamationTriangleIcon, MapPinIcon, UsersIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import ScheduledCoworkersList from 'pollworker/src/components/ScheduledCoworkersList';
import { renderDataAttribute } from 'pollworker/src/components/WorkSchedules';
import { ConfirmationStatus } from 'pollworker/src/enums/confirmationStatus';
import { editWorkSchedule, getWorkSchedule, getWorkScheduleForEditing } from 'pollworker/src/fetchers';
import { useAppDispatch, useAppSelector } from 'pollworker/src/hooks';
import { setCurrentUserWorkSchedules } from 'pollworker/src/state/schedule';
import { TrainingClassStatus, loadTrainingOverview, selectTrainingClasses } from 'pollworker/src/state/training';
import { apiDateConverter } from 'pollworker/src/utils/apiDateConverter';
import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Flexor, Spinner } from 'shared/src/components';
import { ScreenReaderHidden, ScreenReaderOnly } from 'shared/src/components/Accessibility';
import DateFormatter from 'shared/src/components/DateFormatter';
import {
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'shared/src/components/ui/new';
import { toast } from 'sonner';

type EditableWorkSchedule = {
  Id: string;
  ElectionId: string;
  EVUserId: string;
  AssignTo: string;
  Location: string;
  Precinct: string;
  PrecinctLocationName: string;
  Role: string;
  RoleName: string;
  WorkDate: string;
  StartTime: string;
  StartTime12Hr: string;
  EndTime: string;
  EndTime12Hr: string;
  Notes: string;
  ConfirmationStatus: string;
  ConfirmationReason: string;
  DisplayWorkDate: string;
};

export default function WorkScheduleResponseForm({ workScheduleId, closeModal }: { workScheduleId: string; closeModal: () => void }) {
  const [currentTab, setCurrentTab] = useState<'summary' | 'coworkers'>('summary');
  const [editedWorkSchedule, setEditedWorkSchedule] = useState<EditableWorkSchedule>();
  const [reason, setReason] = useState<string>('');
  const [confirmationStatus, setConfirmationStatus] = useState<string>();
  const [saving, setSaving] = useState<boolean>(false);
  const selectedElectionId = useAppSelector((state) => state.election.electionId);
  const trainingClasses = useAppSelector(selectTrainingClasses);
  const originalStatusRef = useRef<string | undefined>(undefined);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (currentTab !== 'summary' || editedWorkSchedule) return;

    getWorkScheduleForEditing(workScheduleId).then((response) => {
      setReason(response.ConfirmationReason || '');
      setEditedWorkSchedule(response);
      if (confirmationStatus === undefined) setConfirmationStatus(response.ConfirmationStatus);
      if (originalStatusRef.current === undefined) originalStatusRef.current = response.ConfirmationStatus;
    });
  }, [workScheduleId, editedWorkSchedule, currentTab, confirmationStatus]);

  const wasConfirmed = originalStatusRef.current === ConfirmationStatus.Confirmed.toString();

  const affectedTrainings = useMemo(() => {
    if (!editedWorkSchedule) return [];
    return trainingClasses.filter((tc) => tc.Status === TrainingClassStatus.Scheduled && tc.RequiredForRoles.includes(editedWorkSchedule.RoleName));
  }, [trainingClasses, editedWorkSchedule]);

  function uploadEditedWorkSchedule() {
    if (!editedWorkSchedule) return;

    setSaving(true);

    editWorkSchedule({
      ...editedWorkSchedule,
      ConfirmationReason: !isDeclined ? '' : reason,
      ConfirmationStatus: confirmationStatus ? Number.parseInt(confirmationStatus) : null,
      WorkDate: apiDateConverter(editedWorkSchedule.WorkDate),
    })
      .then(() => {
        getWorkSchedule(selectedElectionId).then((schedules) => {
          dispatch(setCurrentUserWorkSchedules(schedules));
        });
        dispatch(loadTrainingOverview(selectedElectionId));
        closeModal();
        toast.success(`Your status has been saved as ${isDeclined ? 'declined' : 'accepted'}`);
      })
      .catch((error: unknown) => {
        toast.error(error instanceof Error && error.message ? error.message : 'An error occurred');
      })
      .finally(() => {
        setSaving(false);
      });
  }

  if (!editedWorkSchedule) {
    return (
      <div className="h-72 flex items-center justify-center space-x-2">
        <Spinner show />
        <span>Loading...</span>
      </div>
    );
  }

  const isDeclined = confirmationStatus ? Number.parseInt(confirmationStatus) === ConfirmationStatus.Declined : false;
  const needsReason = !reason && isDeclined;
  const showSaveTooltip = !confirmationStatus || needsReason;

  return (
    <div>
      <p className="mb-2 text-sm text-gray-700">Accept or decline your work schedule here</p>
      <Tabs value={currentTab} onValueChange={(value) => setCurrentTab(value as 'summary' | 'coworkers')}>
        <TabsList className="w-full [&>button]:flex-1">
          <TabsTrigger value="summary" data-testid="tab-summary" className="gap-1.5">
            <CalendarIcon className="size-4" aria-hidden="true" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="coworkers" data-testid="tab-coworkers" className="gap-1.5">
            <UsersIcon className="size-4" aria-hidden="true" />
            Coworkers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="summary" forceMount className="data-[state=inactive]:hidden">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto space-y-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm my-5 ">
                <div className="relative flex items-center space-x-3 rounded-lg border border-border bg-card px-6 py-5 shadow-sm hover:border-muted-foreground/40">
                  <div className="flex-shrink-0">
                    <MapPinIcon className="h-6 w-6" />
                    <ScreenReaderOnly>Location</ScreenReaderOnly>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div>
                      <span className="absolute inset-0" />
                      <span>{renderDataAttribute(editedWorkSchedule.PrecinctLocationName)}</span>
                    </div>
                  </div>
                </div>
                <div className="relative flex items-center space-x-3 rounded-lg border border-border bg-card px-6 py-5 shadow-sm hover:border-muted-foreground/40">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6" />
                    <ScreenReaderOnly>Date and Time</ScreenReaderOnly>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div>
                      <span className="absolute inset-0" />
                      <div>
                        <div>
                          <DateFormatter dateString={editedWorkSchedule.DisplayWorkDate} withTime={false} />
                        </div>
                        <ScreenReaderHidden>
                          {editedWorkSchedule.StartTime12Hr} &rarr; {editedWorkSchedule.EndTime12Hr}
                        </ScreenReaderHidden>
                        <ScreenReaderOnly>
                          {editedWorkSchedule.StartTime12Hr} until {editedWorkSchedule.EndTime12Hr}
                        </ScreenReaderOnly>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <Label required htmlFor="confirmationStatus" className="text-sm text-foreground font-medium">
                  Are you able to work this schedule?
                </Label>
                <RadioGroup
                  name="confirmationStatus"
                  required
                  value={confirmationStatus?.toString()}
                  onValueChange={setConfirmationStatus}
                  className="flex flex-row flex-wrap items-center gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value={ConfirmationStatus.Confirmed.toString()} id="confirmed" />
                    <Label htmlFor="confirmed" className="font-normal">
                      Accept
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value={ConfirmationStatus.Declined.toString()} id="decline" />
                    <Label htmlFor="decline" className="font-normal">
                      Decline
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              {isDeclined ? (
                <div className="py-2 space-y-3">
                  <div>
                    <Flexor className="text-gray-700 text-sm">
                      <Label data-testid="reason-label" required={isDeclined} htmlFor="reason" className="font-medium">
                        Reason
                      </Label>
                      <span aria-hidden="true">{reason?.length || 0} / 1000</span>
                      <ScreenReaderOnly id="textbox-info">Enter your reason here, {reason?.length || 0} out of 1000 characters used</ScreenReaderOnly>
                    </Flexor>
                    <Textarea
                      id="reason"
                      aria-describedby="textbox-info"
                      rows={3}
                      required={isDeclined}
                      disabled={!isDeclined}
                      name="reason"
                      onChange={({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
                        if (value.length > 1000 || !isDeclined) return;
                        setReason(value);
                      }}
                      value={!isDeclined ? '' : reason}
                    />
                  </div>
                  {wasConfirmed && affectedTrainings.length > 0 && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                      <div className="flex items-center gap-2 text-amber-800 font-medium">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                        <span>Declining will cancel the following training:</span>
                      </div>
                      <ul className="mt-2 ml-6 space-y-1 text-amber-700">
                        {affectedTrainings.map((tc) => (
                          <li key={tc.Id} className="list-disc">
                            {tc.ClassName}
                            {tc.SessionDate && <> &mdash; {dayjs(tc.SessionDate).format('MMM D [at] h:mm A')}</>}
                            {tc.SessionLocation && <>, {tc.SessionLocation}</>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
              <Flexor className="pt-3">
                <Button size="sm" variant="link" className="pl-0" onClick={closeModal}>
                  Cancel
                </Button>
                <TooltipProvider>
                  <Tooltip open={showSaveTooltip ? undefined : false}>
                    <TooltipTrigger asChild>
                      <div>
                        <Button size="sm" variant="default" disabled={saving || needsReason || !confirmationStatus} className="space-x-2" onClick={uploadEditedWorkSchedule}>
                          <Spinner light show={saving} />
                          <span>Save</span>
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent align="end" side="top" className="max-w-xs">
                      <ul className="pl-3">
                        {!confirmationStatus ? <li className="list-disc">You must choose Confirm or Decline</li> : null}
                        {needsReason ? <li className="list-disc">You must provide a reason</li> : null}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Flexor>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="coworkers">
          <div className="relative min-h-40 text-sm text-gray-700 mt-3">
            <ScheduledCoworkersList workScheduleId={workScheduleId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
